import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  CardContent,
  TextField,
  Button,
  Box,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
  IconButton,
  Typography,
  Paper
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import axios from 'axios';
import { useAuth } from '../../components/AuthContext';

// Add API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// This could be fetched from API in a real implementation
const GROUPS = [
  { id: 'Development', name: 'Development' },
  { id: 'Operations', name: 'Operations' },
  { id: 'Support', name: 'Support' },
  { id: 'Management', name: 'Management' },
  { id: 'Marketing', name: 'Marketing' },
];

const ComposePost = ({ 
  onPostCreated,
  expanded = false, // New prop to control expansion from parent
  onExpandedChange = null // New callback prop
}) => {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [groupId, setGroupId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Use internal expanded state if not controlled by parent
  const [internalExpanded, setInternalExpanded] = useState(false);
  const { getToken, user } = useAuth();
  
  // Reference to the compose form element
  const composeRef = useRef(null);
  
  // Determine if component is expanded (controlled or uncontrolled)
  const isExpanded = onExpandedChange !== null ? expanded : internalExpanded;
  
  // Function to update expanded state
  const setExpanded = (value) => {
    if (onExpandedChange !== null) {
      onExpandedChange(value);
    } else {
      setInternalExpanded(value);
    }
  };
  
  // Flag to disable outside click handling when select menu is open
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  
  // Replace dialog state with inline confirmation state
  const [showConfirmation, setShowConfirmation] = useState(false);
  
  // Improved event listener for clicks outside the component
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Skip if dialog is open, select dropdown is open, or other conditions
      if (isSelectOpen || !composeRef.current || !isExpanded || showConfirmation) {
        return;
      }
      
      // Check if the click is on a MUI modal or popover (for dropdowns)
      let target = event.target;
      while (target) {
        // Check for MUI popover classes or data attributes
        if (target.classList && (
            target.classList.contains('MuiPopover-root') || 
            target.classList.contains('MuiModal-root') ||
            target.getAttribute('role') === 'presentation' ||
            target.getAttribute('aria-hidden') === 'true'
          )) {
          // Click was on modal/popover, don't close the form
          return;
        }
        target = target.parentElement;
      }
      
      // Check if the click is outside the compose form
      if (!composeRef.current.contains(event.target)) {
        attemptClose();
      }
    };

    // Add event listener if component is expanded
    if (isExpanded) {
      // Use a small timeout to avoid immediate execution
      const timer = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);
      
      return () => {
        clearTimeout(timer);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, title, content, groupId, loading, setExpanded, isSelectOpen, showConfirmation]);
  
  // Hide card completely when not expanded
  if (!isExpanded) {
    return null;
  }
  
  // Format name with proper capitalization
  const formatName = (name) => {
    if (!name) return '';
    return name.split(' ')
      .map(part => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  };

  // Function to extract hashtags from content - improved to handle more edge cases
  const extractHashtags = (text) => {
    if (!text) return [];
    
    // Updated regex to get hashtags at word boundaries
    const hashtagRegex = /#(\w+)/g;
    const matches = text.match(hashtagRegex);
    
    if (!matches) return [];
    
    // Extract just the tag text without the # symbol and ensure uniqueness
    const uniqueTags = [...new Set(matches.map(tag => tag.substring(1).toLowerCase()))];
    console.log('Extracted hashtags:', uniqueTags);
    return uniqueTags;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate input
    if (!title.trim() || !content.trim() || !groupId) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const token = getToken();
      
      // Fetch current user's full name before posting
      let authorFullName = user?.username;
      
      try {
        const apiUrl = process.env.REACT_APP_STATUS_API_URL || '';
        const userResponse = await axios.get(
          `${apiUrl}/user`, 
          { 
            params: { username: user?.username, token },
            headers: { Authorization: `Bearer ${token}` } 
          }
        );
        
        if (userResponse.data?.fornavn && userResponse.data?.etternavn) {
          const firstName = formatName(userResponse.data.fornavn);
          const lastName = formatName(userResponse.data.etternavn);
          authorFullName = `${firstName} ${lastName}`;
        }
      } catch (error) {
        console.error('Error fetching user info:', error);
        // Continue with posting even if name fetch fails
      }
      
      // Extract hashtags from content with the improved function
      const hashtags = extractHashtags(content);
      console.log('Hashtags for new post:', hashtags);
      
      // Create post data with all necessary fields
      const postData = { 
        title,
        content, 
        group_id: groupId,
        // Store the full name as the post creator in database
        // This will be returned when fetching posts, so we don't need to fetch user data again
        created_by: user?.username, // Keep original username for reference
        display_name: authorFullName, // Send the full name to be stored in database
        hashtags: hashtags // Add the extracted hashtags - ensure it's an array
      };
      
      console.log('Posting with display name:', authorFullName);
      
      // Update URL to use API_BASE_URL
      const response = await axios.post(`${API_BASE_URL}/feed`, 
        postData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Ensure the created post has the display name for immediate display
      const newPost = {
        ...response.data,
        author_name: authorFullName,
        hashtags: hashtags // Explicitly include hashtags for frontend even if API doesn't return them
      };
      
      // Log the created post for debugging
      console.log('Created new post with hashtags:', newPost);
      
      onPostCreated(newPost);
      
      // Clear form after successful post
      setTitle('');
      setContent('');
      setGroupId('');
      setExpanded(false); // Collapse the form after successful post
    } catch (err) {
      console.error('Error creating post:', err);
      setError('Failed to create post. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => setError(null);

  // Attempt to close the form - show inline confirmation
  const attemptClose = () => {
    if ((title.trim() || content.trim() || groupId) && !loading) {
      setShowConfirmation(true);
    } else if (!loading) {
      closeForm();
    }
  };
  
  // Actually close the form and reset the fields
  const closeForm = () => {
    setExpanded(false);
    setTitle('');
    setContent('');
    setGroupId('');
    setError(null);
    setShowConfirmation(false);
  };
  
  // Cancel the close operation
  const cancelClose = () => {
    setShowConfirmation(false);
  };

  // Modified handleCancel to use new attemptClose
  const handleCancel = () => {
    attemptClose();
  };

  return (
    <Card 
      ref={composeRef} 
      sx={{ 
        mb: 4, 
        borderRadius: 2,
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.08)',
        border: '1px solid #e0e0e0',
        transition: 'all 0.3s ease'
      }}
    >
      <CardContent sx={{ p: 2 }}>
        {showConfirmation ? (
          // Inline confirmation UI
          <Box>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mb: 2
            }}>
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="h6">Unsaved Changes</Typography>
            </Box>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Your post hasn't been published. If you close now, your changes will be lost.
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button 
                variant="outlined"
                onClick={cancelClose}
              >
                Continue Editing
              </Button>
              <Button 
                variant="contained" 
                color="error"
                onClick={closeForm}
              >
                Discard Changes
              </Button>
            </Box>
          </Box>
        ) : (
          // Normal compose UI
          <form onSubmit={handleSubmit}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mb: 2
            }}>
              <Typography variant="h6">Create New Post</Typography>
              <IconButton 
                size="small" 
                onClick={handleCancel}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Box>
            
            {error && (
              <Alert 
                severity="error" 
                sx={{ mb: 2 }}
                action={
                  <IconButton
                    aria-label="close"
                    color="inherit"
                    size="small"
                    onClick={clearError}
                  >
                    <CloseIcon fontSize="inherit" />
                  </IconButton>
                }
              >
                {error}
              </Alert>
            )}
            
            <Box sx={{ display: 'flex', mb: 2 }}>
              <TextField
                select
                label="Group"
                value={groupId}
                onChange={(e) => setGroupId(e.target.value)}
                sx={{ mr: 2, minWidth: 150 }}
                size="small"
                disabled={loading}
                // Add handlers for select open/close events
                onOpen={() => setIsSelectOpen(true)}
                onClose={() => setIsSelectOpen(false)}
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: 'bottom',
                      horizontal: 'left',
                    },
                    // Fix: add container property and keep tabs in document
                    container: document.body,
                    keepMounted: true
                  }
                }}
              >
                {GROUPS.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </TextField>
              
              <TextField
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                fullWidth
                size="small"
                disabled={loading}
                sx={{ flexGrow: 1 }}
              />
            </Box>
            
            <TextField
              label="What's on your mind?"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              multiline
              rows={3}
              fullWidth
              disabled={loading}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: content.length > 0 && (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setContent('')} edge="end">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
              >
                Post
              </Button>
            </Box>
          </form>
        )}
      </CardContent>
    </Card>
  );
};

export default ComposePost;
